// Settings are read on many screens. Keep one copy and refresh it after changes.

import { useEffect, useState } from 'preact/hooks';
import { api, errorText } from './api';
import type { ProviderStatus, Settings } from '../../shared/types';

export interface SettingsBundle {
  settings: Settings;
  providers: ProviderStatus[];
  appEnv: string;
}

let cached: SettingsBundle | null = null;
let pending: Promise<SettingsBundle> | null = null;
let failed: string | null = null; // why the last load failed, until one works
const subs = new Set<() => void>();

const notify = () => {
  for (const f of subs) f();
};

export function loadSettings(force = false): Promise<SettingsBundle> {
  if (cached && !force) return Promise.resolve(cached);
  if (!pending || force) {
    const p: Promise<SettingsBundle> = api<SettingsBundle>('/api/settings').then(
      (s) => {
        failed = null;
        setSettingsBundle(s);
        return s;
      },
      (e) => {
        failed = errorText(e);
        notify();
        throw e;
      },
    );
    pending = p;
    p.finally(() => pending === p && (pending = null)).catch(() => {});
  }
  return pending;
}

export function setSettingsBundle(s: Partial<SettingsBundle> & { settings: Settings }): void {
  cached = { appEnv: cached?.appEnv || 'prod', providers: cached?.providers || [], ...s } as SettingsBundle;
  notify();
}

/** Settings plus, when there are none yet, why loading failed and a way to try again. */
export function useSettingsState(): { bundle: SettingsBundle | null; error: string | null; retry: () => void } {
  const [, tick] = useState(0);
  useEffect(() => {
    const f = () => tick((n) => n + 1);
    subs.add(f);
    loadSettings().catch(() => {});
    return () => {
      subs.delete(f);
    };
  }, []);
  const retry = () => {
    failed = null;
    notify();
    loadSettings(true).catch(() => {});
  };
  return { bundle: cached, error: cached ? null : failed, retry };
}

export function useSettings(): SettingsBundle | null {
  return useSettingsState().bundle;
}

/** The sender that will be used, or null if none is ready. */
export function readySender(b: SettingsBundle | null): ProviderStatus | null {
  return b?.providers.find((p) => p.enabled && p.ready) ?? null;
}
